/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import type {
    Instance,
    RecaptchaProviderProps,
    RecaptchaContextParams,
} from './recaptcha.types';

import {
    checkRecaptchaInjected,
    injectRecaptchaScript,
    removeRecaptchaBadge,
    removeRecaptchaScript,
    hideRecaptchaBadge,
} from './utils';

import { RecaptchaContext } from './RecaptchaContext';

const onLoadCallbackName = 'onRecaptchaLoad';

/**
 * RecaptchaProvider
 *
 * Provides the grecaptcha.enterprise instance and v3 execution capability.
 * This is a singleton provider that should be placed at the app root.
 *
 * For fallback handling (v3 → v2), use the useRecaptchaWithFallback hook
 * which provides isolated fallback state per usage instance.
 *
 * @example
 * <RecaptchaProvider
 *   v3SiteKey={process.env.RECAPTCHA_ENTERPRISE_KEY_INVIS}
 *   v2SiteKey={process.env.RECAPTCHA_ENTERPRISE_KEY}
 * >
 *   <App />
 * </RecaptchaProvider>
 */
export function RecaptchaProvider({
    v3SiteKey = process.env.RECAPTCHA_ENTERPRISE_KEY_INVIS,
    v2SiteKey = process.env.RECAPTCHA_ENTERPRISE_KEY,
    host = 'google.com',
    scriptProps,
    onLoad,
    onError,
    children,
}: RecaptchaProviderProps) {
    const [recaptchaInstance, setRecaptchaInstance] = React.useState<Instance>();
    const [isRecaptchaReady, setIsRecaptchaReady] = React.useState(false);

    // Load the recaptcha script on mount
    React.useEffect(() => {
        const scriptId = scriptProps?.id ?? 'recaptcha-script';
        const isRecaptchaInjected = checkRecaptchaInjected();

        const onload = () => {
            // Access the grecaptcha.enterprise instance from window
            const windowWithRecaptcha = window as Window & {
                grecaptcha?: { enterprise?: Instance };
            };
            const recaptcha = windowWithRecaptcha.grecaptcha?.enterprise;

            if (!recaptcha) {
                if (onError) onError();
                return;
            }

            // Hide the default badge (we'll show our own v2 checkbox when needed)
            hideRecaptchaBadge();

            recaptcha.ready(async () => {
                setRecaptchaInstance(recaptcha);
                if (onLoad) await onLoad(recaptcha);
                setIsRecaptchaReady(true);
            });
        };

        // Set up the callback for script load on the global window object
        // The recaptcha script expects a global callback function
        Object.defineProperty(window, onLoadCallbackName, {
            value: onload,
            writable: true,
            configurable: true,
        });

        // Check if grecaptcha.enterprise is available on the window
        const windowWithRecaptcha = window as Window & {
            grecaptcha?: { enterprise?: Instance };
        };
        const isRecaptchaAvailable =
            typeof windowWithRecaptcha.grecaptcha?.enterprise !== 'undefined';

        if (isRecaptchaAvailable) {
            // Script is loaded and grecaptcha is available, call onload immediately
            onload();
        } else {
            // Either script doesn't exist, or it exists but hasn't loaded yet.
            // Remove any existing script and inject fresh to ensure onload callback is properly attached
            if (isRecaptchaInjected) {
                removeRecaptchaScript();
            }

            // Load script with v3SiteKey so execute() works for invisible challenges.
            // The v2 checkbox can still be rendered separately with its own site key.
            injectRecaptchaScript({
                isEnterprise: true,
                host,
                render: v3SiteKey,
                ...scriptProps,
                onload,
                id: scriptId,
            });
        }

        return () => {
            if (checkRecaptchaInjected()) {
                removeRecaptchaScript();
            }
            removeRecaptchaBadge();
        };
    }, [host, v3SiteKey, scriptProps, onLoad, onError]);

    /**
     * Execute v3 invisible reCAPTCHA and return the token.
     * This is the raw v3 execution - for fallback handling, use useRecaptchaWithFallback.
     */
    const executeV3 = React.useCallback(
        async (action: string): Promise<string> => {
            if (!recaptchaInstance?.execute) {
                throw new Error('Recaptcha has not been loaded');
            }

            return recaptchaInstance.execute(v3SiteKey, { action });
        },
        [recaptchaInstance, v3SiteKey]
    );

    // Build the context value - provides instance and v3 execution
    const value: RecaptchaContextParams = React.useMemo(
        () => ({
            instance: recaptchaInstance,
            isRecaptchaReady,
            v3SiteKey,
            v2SiteKey,
            executeV3,
        }),
        [recaptchaInstance, isRecaptchaReady, v3SiteKey, v2SiteKey, executeV3]
    );

    return (
        <RecaptchaContext.Provider value={value}>{children}</RecaptchaContext.Provider>
    );
}
